import React, {useState} from 'react';
import {useTranslation, withTranslation} from "react-i18next";
import {ReactComponent as BasketIco} from "../../../../../images/icons/basket-fast.svg";
import {ReactComponent as CalendarIco} from "../../../../../images/icons/calendar.svg";
import ChangePickDateDrawer from "../../../Drawer/ChangePickDateDrawer";
import {isAnotherDateAvailable} from "../../utils";
import {connect, DispatchProp} from "react-redux";
import {RootState} from "../../../../../reducers";
import {getAvailabilities, getItems, getMenus, getPromotions} from "../../../../services/Menu/reducer";
import {getSession, selectLocale} from "../../../../services/Session/reducer";
import AvailableHelper from "../../../../services/Menu/services/Availability/AvailableHelper";
import DateService from "../../../../services/Common/Date/DateService";
import {DateHelper} from "../../../../services/Common/Date/DateHelper";
import HoursHelper from "../../../../services/Menu/services/Availability/HoursHelper";
import ErrorAlertView from "../../../Common/ErrorAlertView";
import {OrderType} from "../../../../services/Api/types";
import {getStoreConfig} from "../../../../services/Store/reducer";
import {ReactComponent as EditIco} from "../../../../../images/icons/edit.svg";
import {createKeyDownHandler} from "../../../../utils/keyDownHanlder";
import {getParsedSessionOrderType} from "../../../../utils/order";
import ItemHelper from "../../../../services/Menu/services/Item/ItemHelper";
import {getCart} from "../../../../services/Cart/reducer";
import DisabledCartItemsModal from "../DisabledCartItemsModal/DisabledCartItemsModal";
import ItemViewHelper from "../../../../services/Menu/services/Item/ItemViewHelper";
import StoreUtils from "../../../../services/Store/StoreUtils";

interface OwnProps {
    time: string | undefined;
    onChange: (time: string | undefined) => void;
    currentType: OrderType | undefined;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

const CartChangeOrderDetailsTimeModal = ({
                                             time,
                                             onChange,
                                             locale,
                                             availabilities,
                                             menus,
                                             currentType,
                                             config,
                                             promotions,
                                             cart,
                                             items,
                                             session
                                         }: Props) => {
    const {t} = useTranslation();
    const parsedCurrentType = getParsedSessionOrderType(currentType);
    const [showPickDateDrawer, setShowPickDateDrawer] = useState(false);
    const allMenuAvailabilities = AvailableHelper.getAvailabilities(availabilities, menus.map((menu: any) => menu.availability_id));
    const orderTypeMenuAvailabilities = AvailableHelper.getAvailabilities(
        availabilities,
        menus.map((menu: any) => parsedCurrentType && menu[parsedCurrentType.toLowerCase()] && menu.availability_id)
    );
    const orderTypeMenuDateAvailabilities = AvailableHelper.geDateAvailableAvailabilities(
        availabilities,
        menus.map((menu: any) => parsedCurrentType && menu[parsedCurrentType.toLowerCase()] && menu.availability_id),
        DateService.emptyOrNow(time)
    );
    const [disabledCartItems, setDisabledCartItems] = useState<any[]>([]);
    const [lastTime, setLastTime] = useState<string | undefined>(time);

    const getPossibleDisabledItemsForTime = (date: string | undefined) => {
        const isOpen = HoursHelper.isOpen(config?.hours || [], DateService.emptyOrNow(date));

        if (!isOpen) {
            return cart.items.map((cartItem: any) => ItemHelper.getItem(items, cartItem.item_id)).filter(Boolean);
        }

        const cartItems = cart.items.map((cartItem: any) => {

            const item = ItemHelper.getItem(items, cartItem.item_id);

            if (!item) return undefined;

            return {
                item,
                itemView: ItemViewHelper.parseItem(item, availabilities, promotions, date, undefined, cart, currentType as OrderType, {
                    skipType: true
                })
            }
        }).filter(Boolean);

        return cartItems.filter(item => item?.itemView?.disabled || !HoursHelper.isOpen(orderTypeMenuDateAvailabilities.flatMap((availability: any) => availability.hours), DateService.emptyOrNow(date))).map(item => item?.item).filter(Boolean);
    }

    const handleSaveDate = (date: string, skipCheckingItems?: true) => {
        setLastTime(date);

        if (!skipCheckingItems) {
            const possibleDisabledItems = getPossibleDisabledItemsForTime(date);
            if (possibleDisabledItems.length > 0) {
                setDisabledCartItems(possibleDisabledItems);
                return;
            }
        }

        onChange(date);
        setShowPickDateDrawer(false);
    }

    const handleAsSoonAsPossibleOptionChange = () => {
        setLastTime(undefined);

        const possibleDisabledItems = getPossibleDisabledItemsForTime(undefined);
        if (possibleDisabledItems.length > 0) {
            setDisabledCartItems(possibleDisabledItems);
            return;
        }

        onChange(undefined);
    }

    const drawInfoAboutNearestOpenHours = () => {
        const isOpen = HoursHelper.isOpen(config?.hours || [], DateService.emptyOrNow(time));
        const canChangePickUpDate = StoreUtils.isPickDateEnabled(config);
        let info = "";
    
        if (config && currentType && !StoreUtils.getOrderTypes(config).includes(currentType)) {
            info = t("modules.cart.field.not_supported_order_type_info.title", {orderType: t(`enums.order.type.${currentType}`)});
        } else if (!canChangePickUpDate && time) {
            const date = DateHelper.getFormattedNearestDate(time, t, locale ?? "");
            info = t("modules.cart.field.not_supported_time_info.title", {date});
        } else if (!isOpen) {
            const nearestOpenHours = AvailableHelper.getNearestOpenDateFromNowOrDate(allMenuAvailabilities)?.dateFrom;
            if (nearestOpenHours) {
                const date = DateHelper.getFormattedNearestDate(DateHelper.format(nearestOpenHours), t, locale ?? "")
                if (canChangePickUpDate) {
                    info = `${t("modules.cart.field.close_now_info.title", {date})}. ${t("modules.cart.field.change_date_to_order.title")}.`;
                } else {
                    info = `${t("modules.cart.field.close_now_info.title", {date})}.`;
                }
            }
        } else {
            const isOpenForCurrentType = HoursHelper.isOpen(orderTypeMenuDateAvailabilities.flatMap((availability: any) => availability.hours), DateService.emptyOrNow(time));

            if (!isOpenForCurrentType) {
                const nearestOpenHours = AvailableHelper.getNearestOpenDateFromNowOrDate(orderTypeMenuAvailabilities)?.dateFrom;

                if (nearestOpenHours) {
                    const date = DateHelper.getFormattedNearestDate(DateHelper.format(nearestOpenHours), t, locale ?? "");
                    if (canChangePickUpDate) {
                        info = `${t("modules.cart.field.close_now_info_for_order_type.title", {orderType: t(`enums.order.type.${currentType}`)})}. ${t("modules.cart.field.change_date_for_order_type_to_order.title", {date})}`;
                    } else {
                        info = `${t("modules.cart.field.close_now_info_for_order_type.title", {orderType: t(`enums.order.type.${currentType}`)})}. ${t("modules.cart.field.go_back_in_another_time.title", {date})}`;
                    }
                } else {
                    info = `${t("modules.cart.field.close_now_info_for_order_type.title", {orderType: t(`enums.order.type.${currentType}`)})}.`;
                }
            }
        }

        if (info) {
            return (
                <ErrorAlertView message={info}/>
            )
        }
    }

    const handleOnLabelKeyDown = (time: boolean) => createKeyDownHandler<HTMLLabelElement>({
        keys: ["Enter", " "],
        preventDefault: true,
        action: () => {
            if (time) {
                setShowPickDateDrawer(true);
            } else {
                handleAsSoonAsPossibleOptionChange()
            }
        }
    });

    const handleOnHideModal = () => {
        setDisabledCartItems([]);
    }

    const handleOnSubmitModal = () => {
        if (lastTime) {
            handleSaveDate(lastTime, true);
        } else {
            onChange(undefined);
        }
        handleOnHideModal();
    }

    const anotherDateAvailable = isAnotherDateAvailable(config, menus, parsedCurrentType);

    return (
        <>
            <ChangePickDateDrawer pickDate={time}
                                  showDrawer={showPickDateDrawer}
                                  handleSaveDate={handleSaveDate}
                                  onHide={() => setShowPickDateDrawer(false)}
                                  higherZIndex
                                  withoutAsSoonAsPossibleOption
                                  orderType={currentType}/>
            {disabledCartItems.length > 0 && <DisabledCartItemsModal show={disabledCartItems.length > 0}
                                                                     items={disabledCartItems}
                                                                     type="PICKUP_AT"
                                                                     pickupAt={lastTime}
                                                                     onSubmit={handleOnSubmitModal}
                                                                     onHide={handleOnHideModal}/>}
            <div className="mt-4 d-flex flex-column gap-2 change-order-details-time">
                <h1 className="h5 mb-2">{t("modules.cart.field.pickup_date.title")}</h1>
                <label tabIndex={0} aria-checked={!time} onKeyDown={handleOnLabelKeyDown(false)}
                       className={`btn btn-default btn-lg btn-check-full ${!time ? " active" : ""}`}>
                    <BasketIco/>
                    <span>
                        {t("common.word.as_soon_as_possible")}
                    </span>
                    <input className="btn-check1" type="radio" name="time" value={undefined} autoComplete="off"
                           tabIndex={-1}
                           checked={!time} onChange={() => handleAsSoonAsPossibleOptionChange()}/>
                </label>
                {anotherDateAvailable && (
                    <label tabIndex={0} aria-checked={!time} onKeyDown={handleOnLabelKeyDown(true)}
                           className={`btn btn-default btn-lg btn-check-full ${time ? " active" : ""}`}>
                        <CalendarIco/>
                        <span>
                        <p className="mb-0">{t("modules.cart.field.another_date.title")}</p>
                            {time &&
                                <small
                                    className="text-primary">{DateHelper.getFormattedNearestDate(time, t, locale ?? "")}</small>}
                    </span>
                        <input style={{width: !!time ? 0 : "unset"}} className="btn-check1" type="radio" name="time"
                               autoComplete="off"
                               tabIndex={-1}
                               checked={!!time} onChange={() => setShowPickDateDrawer(true)}
                               onClick={() => setShowPickDateDrawer(true)}/>
                        {
                            !!time && (
                                <EditIco className="text-primary mx-0"/>
                            )
                        }
                    </label>
                )}
                {
                    !anotherDateAvailable && session.pickup_at && (
                        <label tabIndex={0} aria-checked={!time}
                               className={`btn btn-default btn-lg btn-check-full ${time ? " active" : ""} ${!time ? "disabled" : ""}`}>
                            <CalendarIco/>
                            <span>
                                <p className="mb-0">{t("modules.cart.field.another_date.title")}</p>
                                <small className="text-danger">
                                    {t("common.word.not_available")}
                                </small>
                            </span>
                            <input className="btn-check1" type="radio" name="time"
                                   autoComplete="off"
                                   tabIndex={-1}
                                   checked={!!time}
                            />
                        </label>
                    )
                }
                {drawInfoAboutNearestOpenHours()}
            </div>
        </>
    )
}

const mapStateToProps = (state: RootState) => ({
    availabilities: getAvailabilities(state.menu),
    locale: selectLocale(state.session),
    config: getStoreConfig(state.config),
    cart: getCart(state.cart),
    promotions: getPromotions(state.menu),
    items: getItems(state.menu),
    menus: getMenus(state.menu),
    session: getSession(state.session)
});

const connector = connect(mapStateToProps);
export default withTranslation()(connector(CartChangeOrderDetailsTimeModal));